import React, { Fragment, useRef } from "react";
import { Button, Form, InputRef, Select, SelectProps, Space, Tag } from "antd";
import {
  Controller,
  Control,
  FieldValues,
  PathValue,
  Path,
} from "react-hook-form";
import { InfoIconOutlined, SelectArrow, TickIcon } from "../../CustomIcons";
import { PlusOutlined } from "@ant-design/icons";

type TagRender = SelectProps["tagRender"];

const { Option } = Select;

interface materialOptionType {
  value: string;
  label: string;
  color?: string;
  type: string;
}

interface materialDropDownType {
  label: string;
  options: materialOptionType[];
}

interface SelectFieldProps<T extends FieldValues> {
  fieldName: Path<T>;
  label?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Path<T>> | undefined;
  iProps?: SelectProps<string | number | readonly string[]>;
  isRequired?: boolean;
  rules?: object;
  validate?: "" | "success" | "warning" | "error" | "validating" | undefined;
  validMessage?: string | React.ReactNode;
  className?: string;
  prefix?: React.ReactNode;
  tooltipTitle?: string | React.ReactNode; // Use ReactNode for tooltip title
  optionalLabel?: string;
  hintMessage?: string;
  warning?: boolean;
  onSelectChange?: (
    value: string | number | readonly string[],
    options: { value: string; label: string; color?: string; type: string }
  ) => void;
  selectOption: materialDropDownType[];
  addItemText: string;
  onAddItemClick?: () => void;
}

export const SelectOptionWithButton = <T extends FieldValues>({
  fieldName,
  isRequired = false, // Provide default value
  label,
  validMessage,
  className,
  control,
  defaultValue, // Provide default value
  rules,
  iProps,
  prefix,
  tooltipTitle,
  optionalLabel,
  hintMessage,
  warning,
  validate,
  onSelectChange,
  selectOption,
  addItemText,
  onAddItemClick,
}: SelectFieldProps<T>) => {
  const inputRef = useRef<InputRef>(null);
  const optionMap = selectOption?.reduce<
    Record<string, { label: string; value: string; color?: string }>
  >((acc, optionGroup) => {
    optionGroup.options.forEach((option) => {
      acc[option.value] = option;
    });
    return acc;
  }, {});

  const tagRender: TagRender = (props) => {
    const { value } = props;
    const optionData = optionMap[value];
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return optionData?.color ? (
      <Tag
        className={optionData?.color ?? "default_tag"}
        onMouseDown={onPreventMouseDown}
      >
        {optionData?.label}
      </Tag>
    ) : (
      <div>{optionData?.label}</div>
    );
  };

  const addItem = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (onAddItemClick) {
      onAddItemClick();
    }
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Form.Item
      required={isRequired ?? false}
      label={<div className="label_text">{label}</div>}
      validateStatus={validate}
      help={validMessage}
      className={className}
      tooltip={
        tooltipTitle && {
          title: tooltipTitle,
          placement: "topLeft",
          arrow: false,
          icon: <InfoIconOutlined />,
        }
      }
    >
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              popupClassName="option-group-select"
              suffixIcon={<SelectArrow />}
              {...(iProps && { ...iProps, size: undefined })}
              value={value ? value : null}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              optionFilterProp="label"
              prefix={prefix}
              onChange={(val) => {
                onChange(val);
                if (onSelectChange) {
                  selectOption?.some((innerArray) => {
                    const foundInInnerArray = innerArray?.options?.find(
                      (element: materialOptionType) => element?.value === val
                    );

                    if (foundInInnerArray) {
                      onSelectChange(val, foundInInnerArray);
                      return true; // Ensure a boolean is returned
                    }
                    return false; // Explicitly return false if not found
                  });
                }
              }}
              tagRender={tagRender}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Space
                    style={{
                      padding: "6px 12px",
                      borderRadius: "8px",
                      border: "1px solid #DEE5E6",
                      color: "#151718",
                      fontSize: "13px",
                      fontWeight: "600",
                      marginLeft: "2px",
                      marginTop: "7px",
                      lineHeight: "16px",
                    }}
                  >
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={addItem}
                    >
                      {addItemText}
                    </Button>
                  </Space>
                </>
              )}
            >
              {selectOption?.length > 0 &&
                selectOption?.map(
                  (
                    resp: {
                      label: string;
                      options: materialOptionType[];
                    },
                    i: number
                  ) => (
                    <Fragment key={i}>
                      <Option value={resp?.label} label={resp?.label} disabled>
                        {resp?.label}
                      </Option>
                      {resp?.options?.length > 0 &&
                        resp?.options?.map((e: materialOptionType) => {
                          return (
                            <Fragment key={e?.value}>
                              <Option value={e?.value} label={e?.label}>
                                {e?.color ? (
                                  <Tag
                                    style={{ padding: "2px 6px" }}
                                    className={e?.color ?? "default_tag"}
                                  >
                                    {e?.label}
                                  </Tag>
                                ) : (
                                  e?.label
                                )}
                              </Option>
                            </Fragment>
                          );
                        })}
                    </Fragment>
                  )
                )}
            </Select>
            {optionalLabel && (
              <div className="optional-label">{optionalLabel}</div>
            )}
            {hintMessage && (
              <div
                className={warning ? "hint-label warning-label" : "hint-label"}
              >
                {hintMessage}
              </div>
            )}
          </>
        )}
      />
    </Form.Item>
  );
};
